/** @jsxImportSource @emotion/react */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { tabs } from 'components/layouts/Tab';
import { Code } from 'models/common/CommonCode';
import { UtMatrixRegistSearchCondition, UtMatrixMaster, UtMatrixDetail } from 'models/ut/UtMatrix';
import {
  findMaster,
  findProcess,
  findProcessBar,
  submitCheckRegistMatrix,
  submitUtMatix,
  changeStatUtMatix,
  submitCheckReviewMatrix,
} from 'apis/ut/UtMatrixRegist';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import MachineSummaryContent from './review/MachineSummaryContent';
import ElectricSummaryContent from './review/ElectricSummaryContent';
import StandardUseContent from './review/StandardUseContent';
import DetailSummaryContent from './review/DetailSummaryContent';

import CustomGrid from '../../components/grids/CustomGrid';
import { UtMatrixSummaryList } from '../../models/ut/UtMatrixSummary';
import { getUtMatrixSummaryList } from '../../apis/ut/UtMatrixSummary';
import { ManagementMode } from '../../models/common/Common';
import UtMatrixGraphPopup from './popup/UtMatrixGraphPopup';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

interface PageLocationState {
  openMode?: 'UT_MGR' | 'WRITE' | 'READ';
  utmId?: string;
  planProcId?: string;
}

interface Props {
  aprReqId: string; // 결재요청ID
  mode: ManagementMode;
  // requestMasterList: EquipmentClassificationMaster[];
  isReadOnly?: boolean;
  utmId?: string;
}

/**
 * @desc UT Matrix 결재 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.04   jshwang12      자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2025.10.04   jshwang12      N2 AVG 추가
 * 2026.08.05   jihwan89       CSR E20260727-000228 Vacuum Pump 추가
 */
const UtMatrixReviewRequest = (props: Props, ref) => {
  const { t } = useTranslation();
  const detailRef = useRef<any>();
  const machineRef = useRef<any>();
  const elecRef = useRef<any>();
  const atulCapaRef = useRef<any>();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const [isOpenGraphModal, setOpenGraphModal] = useState<boolean>(false);
  const [utmData, setUtmData] = useState<UtMatrixSummaryList[]>([]);
  const conditionRef = useRef<UtMatrixRegistSearchCondition>();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<UtMatrixRegistSearchCondition>({
    utmId: props?.utmId || searchParams?.get('utmId') || '',
    // planProcId: searchParams?.get('planProcId') || '',
    openMode: searchParams?.get('openMode') || 'appr',
    sumBaseCd: 'ALL',
  });
  const [errors, setErrors] = useState<any>({});
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [masterData, setMasterData] = useState<UtMatrixMaster[]>([]);
  const [processData, setProcessData] = useState<UtMatrixDetail[]>([]);
  const [utmWkProgStatCd, setUtmWkProgStatCd] = useState<Code[]>([]);
  const [utmWkProgInfo, setUtmWkProgInfo] = useState<UtMatrixDetail>();
  const tabMenus = useMemo(() => {
    return [
      { id: 'UTM', name: t('ut.label.UT Matrix', 'UT Matrix') },
      { id: 'MACH', name: t('ut.label.기계요약', '기계요약') },
      { id: 'ELTR', name: t('ut.label.전기요약', '전기요약') },
      { id: 'CAPA', name: t('ut.label.표준사용량 비교', '표준사용량 비교') },
    ];
  }, []);

  // 검색조건 > UT Matrix 명 (ComboBox 사용을 위해 변환)
  const optionsUtMaster = useMemo(() => {
    return (masterData || []).reduce(
      (acc, cur) => [
        ...acc,
        {
          cmnCd: cur.utmId,
          cmnCdNm: cur.utmNm,
        } as Code,
      ],
      [] as Code[]
    );
  }, [masterData]);

  const selectedMaster = useMemo(() => {
    if (!condition?.utmId) return null;
    const master = (masterData || []).filter((o) => o.utmId === condition?.utmId);
    return master.length > 0 ? master[0] : null;
  }, [condition?.utmId, masterData]);

  useEffect(() => {
    setCondition((prev) => ({
      utmId: props?.utmId || locationState?.utmId || searchParams?.get('utmId') || '',
      // planProcId: locationState?.planProcId || searchParams?.get('planProcId') || '',
      openMode: locationState?.openMode || searchParams?.get('openMode') || '',
      sumBaseCd: prev?.sumBaseCd || 'ALL',
      aprReqId: props.aprReqId,
    }));
  }, [locationState?.utmId, props]);

  useEffect(() => {
    if (state?.utmId) {
      setLocationState(state);
    }
  }, [state]);

  useEffect(() => {
    findMaster().then((result: UtMatrixMaster[]) => {
      if (result !== null) {
        setMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getCommonCodes();
  }, []);

  useEffect(() => {
    setCondition((prev) => ({
      ...prev,
      prdnProcCd: '',
      eqclId: '',
    }));
    if (!condition?.utmId) {
      setProcessData([]);
      return;
    }
    searchProcess(condition?.utmId);
    handleSearch();
  }, [condition?.utmId]);

  const getCommonCodes = async () => {
    const utmWkProgStatCd: Code[] = await getCommonCodeNames('UTM_WK_PROG_STAT_CD');
    const sumBaseCd: Code[] = await getCommonCodeNames('SUM_BASE_CD');

    setUtmWkProgStatCd((utmWkProgStatCd || []).filter((o) => o.cmnCd !== 'UTM06'));
    setCode({
      sumBaseCd: sumBaseCd,
    });
  };

  const handleSearch = () => {
    setErrors({
      utmId: _.isEmpty(condition.utmId),
      sumBaseCd: _.isEmpty(condition.sumBaseCd),
    });

    if (_.isEmpty(condition.utmId) || _.isEmpty(condition.sumBaseCd)) {
      return;
    }
    conditionRef.current = condition;
    searchProcessBar(condition.utmId);
    searchSummary(condition);
    searchTabContent();
  };

  useEffect(() => {
    if (conditionRef.current && conditionRef.current?.utmId) {
      searchTabContent();
    }
  }, [currentTabIndex]);

  useEffect(() => {
    if ((processData || []).length) {
      let proc = [] as UtMatrixDetail[];
      if (condition?.prdnProcCd && condition?.eqclId) {
        proc = processData.filter(
          (o) => o.prdnProcCd === condition?.prdnProcCd && o.eqclId === condition?.eqclId
        );
      } else if (condition?.planProcId) {
        proc = processData.filter((o) => o.planProcId === condition?.planProcId);
      }
      if (proc.length) {
        setCondition((prev) => ({
          ...prev,
          planProcId: proc[0].planProcId,
          prdnProcCd: proc[0].prdnProcCd,
          prdnProcCdNm: proc[0].prdnProcCdNm,
          eqclId: proc[0].eqclId,
          eqclNm: proc[0].eqclNm,
          utmWrtProcProgStatCd: proc[0].utmWrtProcProgStatCd,
          utmWrtProcProgStatCdNm: proc[0].utmWrtProcProgStatCdNm,
        }));
      }
    }
  }, [processData, condition?.prdnProcCd, condition?.eqclId, condition?.planProcId]);

  const processSteps = useMemo(() => {
    let currentStepNo = -1;
    return (utmWkProgStatCd || []).reduce((acc, cur, idx) => {
      const isNow = (utmWkProgInfo || {}).utmWkProgStatCd === cur.cmnCd;
      if (isNow) currentStepNo = idx;
      return [
        ...acc,
        {
          ...cur,
          cmnCdDesc: isNow
            ? 'now'
            : Object.keys(utmWkProgInfo || {}).length > 0 && currentStepNo < 0
            ? 'done'
            : '', // or 'disable'
        },
      ];
    }, [] as Code[]);
  }, [utmWkProgStatCd, utmWkProgInfo]);

  const searchProcessBar = (utmId?: string) => {
    if (!utmId) return;
    findProcessBar(utmId).then((result) => {
      setUtmWkProgInfo(result);
      condition.utmWkProgStatCd = result.utmWkProgStatCd;
    });
  };

  const searchProcess = (utmId?: string) => {
    return new Promise((resolve) => {
      if (!utmId) {
        resolve(true);
        return;
      }
      findProcess(utmId)
        .then((result: UtMatrixDetail[]) => {
          if (result !== null) {
            setProcessData(result);
          } else {
            openMessageBar({
              type: 'error',
              content: t(
                'ut.label.공정 정보 조회 중 오류가 발생했습니다.',
                '공정 정보 조회 중 오류가 발생했습니다.'
              ),
            });
          }
        })
        .finally(() => resolve(true));
    });
  };

  const searchTabContent = () => {
    if ((tabMenus || []).length) {
      if (tabMenus[currentTabIndex].id === 'UTM') {
        detailRef.current?.searchRegistMatrix(conditionRef.current);
      } else if (tabMenus[currentTabIndex].id === 'MACH') {
        machineRef.current?.searchMachineSummary(conditionRef.current);
      } else if (tabMenus[currentTabIndex].id === 'ELTR') {
        elecRef.current?.searchElectricSummary(conditionRef.current);
      } else if (tabMenus[currentTabIndex].id === 'CAPA') {
        atulCapaRef.current?.searchStandardUse(conditionRef.current);
      }
    }
  };

  const navigate = useNavigate();

  //보류 링크
  const handleRequestLinkHold = async () => {
    if (!conditionRef.current?.utmId) return;
    navigate('/ut/ut-matrix-request', {
      state: { searchParam: { utmId: summaryRowData[0].utmId, mode: 'HOLD' } },
    });
  };
  //재작성 요청 링크
  const handleRequestLinkRewrite = async () => {
    if (!conditionRef.current?.utmId) return;
    navigate('/ut/ut-matrix-request', {
      state: { searchParam: { utmId: summaryRowData[0].utmId, mode: 'REWRITE' } },
    });
    //결재요청1
  };

  // 검증
  const handleReview = async () => {
    if (!conditionRef.current?.utmId) return;
    submitCheckReviewMatrix(conditionRef.current?.utmId, undefined).then((result) => {
      if (result) {
        openCommonModal({
          modalType: 'alert',
          content: result,
          // yesButtonText: String(t('ut.label.검증', '검증')),
          // yesCallback: () => {
          //   submitUtMatrix();
          // },
        });
      } else {
        // submitUtMatrix();
        openMessageBar({
          type: 'confirm',
          content: t('com.msg.검증되었습니다.', '검증되었습니다.'),
        });
      }
    });
  };

  const submitUtMatrix = () => {
    if (!conditionRef.current?.utmId) return;
    submitUtMatix(conditionRef.current).then((res) => {
      if (res?.successOrNot === 'Y') {
        searchProcess(conditionRef.current?.utmId).then(() => handleSearch());
      }
      openMessageBar({
        type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
        content:
          res?.successOrNot === 'Y'
            ? t('com.msg.검증되었습니다.', '검증되었습니다.')
            : t('com.msg.검증 중 오류가 발생했습니다.', '검증 중 오류가 발생했습니다.'),
      });
    });
  };

  // 검토진행
  const handleSubmitReview = async () => {
    if (!conditionRef.current?.utmId) return;

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.검토진행 하시겠습니까?', '검토진행 하시겠습니까?'),
      yesCallback: () => {
        changeStatUtMatix({ ...conditionRef.current, utmWrtProcProgStatCd: 'UTP04' }).then(
          (res) => {
            if (res?.successOrNot === 'Y') {
              searchProcess(conditionRef.current?.utmId).then(() => handleSearch());
            }
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('ut.label.검토중 상태로 변경되었습니다.', '검토중 상태로 변경되었습니다.')
                  : t('ut.label.수정 중 오류가 발생했습니다.', '수정 중 오류가 발생했습니다.'),
            });
          }
        );
      },
    });
  };

  //결재 요청
  const handleApproval_temp = () => {
    if (!conditionRef.current?.utmId) return;
    //결재요청

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.확정완료 하시겠습니까?', '확정완료 하시겠습니까?'),
      yesCallback: () => {
        changeStatUtMatix({ ...conditionRef.current, utmWrtProcProgStatCd: 'UTP06' }).then(
          (res) => {
            if (res?.successOrNot === 'Y') {
              searchProcess(conditionRef.current?.utmId).then(() => handleSearch());
            }
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('ut.label.확정완료 상태로 변경되었습니다.', '확정완료 상태로 변경되었습니다.')
                  : t('ut.label.오류가 발생했습니다.', '오류가 발생했습니다.'),
            });
          }
        );
      },
    });
  };

  const [summaryRowData, setSummaryRowData] = useState<UtMatrixSummaryList[]>([]);

  const onInitializedSummary = (grid) => {
    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return;

      if (e.target instanceof HTMLButtonElement) {
        const col = grid.columns[ht.col]; // 선택한 컬럼의 정보
        const item = grid.rows[ht.row].dataItem; // 선택한 row의 정
        switch (e.target.id) {
          case 'btnMoveGraph':
            setOpenGraphModal(true);
            setUtmData(item);
            break;
        }
      }
    });
  };

  useImperativeHandle(ref, () => ({
    getBizReqData: (aprReqId: string, reqRsn: string) => {
      // 저장
      const master = {
        castType: 'UtMatrixReviewAppr', // 서버측 결재요청상세vo > bizReqData cast 타입
        aprReqId: aprReqId,
        reqRsn: reqRsn,
        utmId: summaryRowData[0].utmId,
      };
      return master;
    },
  }));

  const searchSummary = async (condition) => {
    if (!condition) return;

    getUtMatrixSummaryList(condition.utmId).then((result) => {
      setSummaryRowData(result);
    });
  };

  const layoutDefinitionSummary = [
    {
      binding: 'utmId',
      header: String(t('ut.label.UTM ID', 'UTM ID')),
      visible: true,
      isReadOnly: true,
      align: 'center',
      minWidth: 90,
      width: 110,
    },
    {
      binding: 'bildPlntCdNm',
      header: String(t('ut.label.공장', '공장')),
      minWidth: 80,
      width: 100,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'bildPlntCd',
      visible: false,
    },
    {
      binding: 'utmNo',
      header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
      minWidth: 80,
      width: 110,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'utmNm',
      header: String(t('ut.label.UT Matrix 명', 'UT Matrix 명')),
      minWidth: 80,
      width: 180,
      isReadOnly: true,
    },
    {
      binding: 'verNo',
      header: String(t('ut.label.버전', '버전')),
      minWidth: 44,
      width: 44,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'lineQty',
      header: String(t('ut.label.Line 수', 'Line 수')),
      minWidth: 60,
      width: 82,
      align: 'center',
      isReadOnly: true,
    },
    {
      binding: 'graph',
      header: String(t('ut.label.Graph', 'Graph')),
      minWidth: 44,
      width: 44,
      align: 'center',
      isReadOnly: true,
      cssClass: 'WijmoPlay',
      cellTemplate: '<button id="btnMoveGraph"></button>',
    },
    {
      header: String(t('ut.label.전기', '전기')),
      columns: [
        {
          binding: 'elec',
          header: '[kW]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.CDA', 'CDA')),
      columns: [
        {
          binding: 'cda',
          header: '[Nl/min]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.N2 Peak', 'N2 Peak')),
      columns: [
        {
          binding: 'n2',
          header: '[Nl/min]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.N2 AVG', 'N2 AVG')),
      columns: [
        {
          binding: 'n2Avg',
          header: '[Nl/min]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.IW', 'IW')),
      columns: [
        {
          binding: 'iw',
          header: '[ℓ/min]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.PCW', 'PCW')),
      columns: [
        {
          binding: 'pcw',
          header: '[ℓ/min]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Steam', 'Steam')),
      columns: [
        {
          binding: 'steam',
          header: '[t/hr]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.NG', 'NG')),
      columns: [
        {
          binding: 'ng',
          header: '[N㎥/hr]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Return air', 'Return air')),
      columns: [
        {
          binding: 'exRetrun',
          header: '[CMN]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Exhaust air', 'Exhaust air')),
      columns: [
        {
          binding: 'exVent',
          header: '[CMN]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
    {
      header: String(t('ut.label.Vacuum Pump', 'Vacuum Pump')),
      columns: [
        {
          binding: 'vp',
          header: '[CMN]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Supply Air', 'Supply air')),
      columns: [
        {
          binding: 'sa',
          header: '[CMN]',
          minWidth: 80,
          width: 96,
          align: 'right',
          isReadOnly: true,
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
  ];

  return (
    <>
      {/* summary grid start */}
      <div>
        <CustomGrid
          layoutDefinition={layoutDefinitionSummary}
          rowData={summaryRowData}
          initialized={onInitializedSummary}
          showMarquee={true}
          isFilter={false}
          isSelector={false}
          excludePin={[
            'verNo',
            'lineQty',
            'graph',
            'elec',
            'cda',
            'n2',
            'n2Avg',
            'iw',
            'pcw',
            'steam',
            'ng',
            'exRetrun',
            'exVent',
            'sa',
          ]}
          height={100}
        />
      </div>
      {/* summary grid end */}

      <div css={tabs.wrap} style={{ marginTop: '24px' }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => {
            // 이전 탭이 Ut Matrix인 경우 자동 저장 후 탭변경
            if (currentTabIndex === 0) {
              detailRef.current?.saveRegistMatrix().finally(() => {
                setCurrentTabIndex(newValue);
              });
            } else {
              setCurrentTabIndex(newValue);
            }
          }}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab
              key={o.id}
              label={o.name}
              id={`ut-tab-${idx}`}
              aria-controls={`ut-tabpanel-${idx}`}
            />
          ))}
        </Tabs>
      </div>

      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <DetailSummaryContent
            ref={detailRef}
            condition={conditionRef.current}
            processData={processData}
            onSubmit={handleSubmitReview}
            onModify={handleReview}
            onLinkHold={handleRequestLinkHold}
            onLinkRewrite={handleRequestLinkRewrite}
          />
        ) : currentTabIndex === 1 ? (
          <MachineSummaryContent
            ref={machineRef}
            condition={conditionRef.current}
            processData={processData}
            onSubmit={handleSubmitReview}
            onModify={handleReview}
            onLinkHold={handleRequestLinkHold}
            onLinkRewrite={handleRequestLinkRewrite}
          />
        ) : currentTabIndex === 2 ? (
          <ElectricSummaryContent
            ref={elecRef}
            condition={conditionRef.current}
            processData={processData}
            onSubmit={handleSubmitReview}
            onModify={handleReview}
            onLinkHold={handleRequestLinkHold}
            onLinkRewrite={handleRequestLinkRewrite}
          />
        ) : (
          <StandardUseContent
            ref={atulCapaRef}
            condition={conditionRef.current}
            processData={processData}
            onSubmit={handleSubmitReview}
            onModify={handleReview}
            onLinkHold={handleRequestLinkHold}
            onLinkRewrite={handleRequestLinkRewrite}
          />
        )}
      </TabPanel>

      {isOpenGraphModal && (
        <UtMatrixGraphPopup
          open={isOpenGraphModal}
          close={() => setOpenGraphModal(false)}
          condition={utmData}
        />
      )}
    </>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`ut-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default forwardRef(UtMatrixReviewRequest);
